/**
 * The integer tiles a mobile's foot overlaps at a fractional (interpolated)
 * position - the two endpoints of the step it is mid-way through, or the single
 * tile it rests on. Pure and rendering-free (no Pixi), so it unit-tests headlessly.
 *
 * The renderer uses this to fix the walk-on-surface draw-order bug (ADR-0168): a
 * flat surface (roof cap, first-floor carpet) on a tile the mobile is stepping
 * across must draw *behind* the mobile for the whole step, so the renderer
 * re-anchors any surface on a straddled tile to the mobile's own depth key. It is
 * deliberately derived from the interpolated **position** alone, not the mobile's
 * facing: a moonwalk steps opposite its facing (ADR-0145), so facing is not the
 * motion direction, and a position-only derivation is correct for every gait.
 *
 * A step moves between two orthogonally- or diagonally-adjacent tiles, so at any
 * instant the foot straddles exactly those two (or one, at rest). Crucially this
 * returns only the two genuine step endpoints and **never** the off-diagonal
 * "phantom" corners of the enclosing 2x2 block - a phantom corner can hold a wall
 * the mobile is walking behind, and re-anchoring that would break its occlusion.
 */

/** How close a fractional coordinate must be to an integer to count as resting on it. */
const EPS = 1e-4;

/**
 * The 1 or 2 integer tiles the foot at (`fx`, `fy`) straddles. Resting (both
 * coordinates integral) yields the single occupied tile. Mid-step yields the two
 * adjacent step endpoints, choosing the correct diagonal so phantom corners are
 * excluded (see the module note).
 */
export function straddledTiles(fx: number, fy: number): Array<[number, number]> {
  const x0 = Math.floor(fx + EPS);
  const y0 = Math.floor(fy + EPS);
  const ax = fx - x0;
  const ay = fy - y0;
  const movingX = ax > EPS;
  const movingY = ay > EPS;
  const x1 = x0 + 1;
  const y1 = y0 + 1;

  if (!movingX && !movingY) return [[x0, y0]]; // at rest on one tile
  if (!movingY) return [[x0, y0], [x1, y0]]; // east/west
  if (!movingX) return [[x0, y0], [x0, y1]]; // north/south
  // Diagonal: the two endpoints share equal per-axis progress (main diagonal,
  // ax approx ay) or complementary progress (anti-diagonal, ax approx 1 - ay).
  // Pick whichever pairing the position sits closer to; the other diagonal's
  // corners are phantoms the foot never actually occupies.
  return Math.abs(ax - ay) <= Math.abs(ax + ay - 1)
    ? [[x0, y0], [x1, y1]]
    : [[x0, y1], [x1, y0]];
}
